import type { GbTier } from "@/lib/pricing-utils";
import { ROUTES } from "@/lib/constants";
import { FALLBACK_RES_TIERS } from "@/components/sections/products/residential-proxies/pricing-tiers/data";
import { FALLBACK_MOBILE_GB_TIERS } from "@/components/sections/products/mobile-proxies/pricing-tiers/data";
import type { ProductsInfo } from "@/lib/products-info";

/**
 * The two per-GB products the Scraping Cost Calculator can price against. Plain data only (no
 * components), so server code such as the /api/md route can import it.
 *
 * `planNote` is shown on the plan card and must match each product's own pricing FAQ: residential
 * data rolls over, mobile per-GB data expires after one month.
 */
export type ProxyType = "residential" | "mobile";

export interface ProxyProduct {
  id: ProxyType;
  label: string;
  /** Key of the tier list inside the /api/products-info response. */
  productsInfoKey: keyof Pick<ProductsInfo, "Monthly Per GB Residential" | "Monthly Per GB Mobile">;
  fallbackTiers: GbTier[];
  pricingHref: string;
  pricingCta: string;
  /** Sentence about the plan's minimum and rollover, shown under the plan size. */
  planNote: string;
}

export const PROXY_PRODUCTS: Record<ProxyType, ProxyProduct> = {
  residential: {
    id: "residential",
    label: "Residential",
    productsInfoKey: "Monthly Per GB Residential",
    fallbackTiers: FALLBACK_RES_TIERS,
    pricingHref: ROUTES.pricingResidentialProxies,
    pricingCta: "Explore residential plans",
    planNote: "Unused data rolls over, and the smallest plan is $25 for 25 GB.",
  },
  mobile: {
    id: "mobile",
    label: "Mobile",
    productsInfoKey: "Monthly Per GB Mobile",
    fallbackTiers: FALLBACK_MOBILE_GB_TIERS,
    pricingHref: ROUTES.pricingMobileProxies,
    pricingCta: "Explore mobile plans",
    planNote: "Unused data does not roll over, and the smallest plan is $50 for 25 GB.",
  },
};

export const DEFAULT_PROXY_TYPE: ProxyType = "residential";

export function parseProxyType(value: string | null): ProxyType {
  return value === "mobile" ? "mobile" : DEFAULT_PROXY_TYPE;
}
